/**
 * Ring buffer of recent console output for support ticket attachments.
 * Also routes console.error / console.warn into diagnosticLogger (replaces diagnosticConsole).
 */

import { diagnosticLogger, type DiagnosticLevel } from './diagnosticLogger';
import { sanitizeDiagnosticText, sanitizeStack } from './diagnosticSanitizer';

const MAX_LINES = 800;
/** Rolling window — drop lines older than this (not a startup-only snapshot). */
const RING_MAX_AGE_MS = 10 * 60 * 1000;
const TRACKING_PREVENTION_RE = /tracking prevention blocked access to storage/i;

export interface ConsoleCaptureLine {
  ts: number;
  level: string;
  text: string;
}

const ring: ConsoleCaptureLine[] = [];

let inInterceptor = false;
let origLog: (...args: unknown[]) => void;
let origInfo: (...args: unknown[]) => void;
let origWarn: (...args: unknown[]) => void;
let origError: (...args: unknown[]) => void;
let origDebug: (...args: unknown[]) => void;

function isNoiseLine(text: string): boolean {
  return TRACKING_PREVENTION_RE.test(text);
}

function formatArgs(args: unknown[]): string {
  return args
    .map((arg) => {
      if (arg == null) return String(arg);
      if (typeof arg === 'string') return arg;
      if (arg instanceof Error) {
        return [arg.name, arg.message, sanitizeStack(arg.stack)].filter(Boolean).join(': ');
      }
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(' ');
}

function pruneConsoleRing(now = Date.now()): void {
  const cutoff = now - RING_MAX_AGE_MS;
  while (ring.length > 0 && ring[0].ts < cutoff) {
    ring.shift();
  }
  if (ring.length > MAX_LINES) {
    ring.splice(0, ring.length - MAX_LINES);
  }
}

function pushLine(level: string, args: unknown[]): void {
  const text = sanitizeDiagnosticText(formatArgs(args).slice(0, 4000));
  if (!text.trim() || isNoiseLine(text)) return;
  const now = Date.now();
  ring.push({ ts: now, level, text });
  pruneConsoleRing(now);
}

function logToDiagnostic(level: 'error' | 'warn', args: unknown[]): void {
  if (inInterceptor) return;
  inInterceptor = true;
  try {
    const message =
      args.find((a) => typeof a === 'string' && a.trim())?.toString().slice(0, 500) ||
      (args[0] instanceof Error ? args[0].message : String(args[0] ?? 'console message')).slice(0, 500);
    if (isNoiseLine(message)) return;
    diagnosticLogger.log({
      level,
      category: level === 'error' ? 'console-error' : 'console-warn',
      message: sanitizeDiagnosticText(message),
    });
  } catch {
    /* swallow */
  } finally {
    inInterceptor = false;
  }
}

function wrapConsoleLevel(
  level: string,
  orig: (...args: unknown[]) => void,
  diagnosticLevel?: 'error' | 'warn',
): (...args: unknown[]) => void {
  return (...args: unknown[]) => {
    orig(...args);
    try {
      pushLine(level, args);
      if (diagnosticLevel) logToDiagnostic(diagnosticLevel, args);
    } catch {
      /* never break console */
    }
  };
}

/** Install once at app init (main.jsx). */
export function installSupportConsoleCapture(): void {
  if (typeof console === 'undefined') return;
  const g = globalThis as typeof globalThis & { __maestroSupportConsoleCaptureInstalled?: boolean };
  if (g.__maestroSupportConsoleCaptureInstalled) return;
  g.__maestroSupportConsoleCaptureInstalled = true;

  origLog = console.log.bind(console);
  origInfo = console.info.bind(console);
  origWarn = console.warn.bind(console);
  origError = console.error.bind(console);
  origDebug = console.debug.bind(console);

  console.log = wrapConsoleLevel('log', origLog);
  console.info = wrapConsoleLevel('info', origInfo);
  console.warn = wrapConsoleLevel('warn', origWarn, 'warn');
  console.error = wrapConsoleLevel('error', origError, 'error');
  console.debug = wrapConsoleLevel('debug', origDebug);
}

export function getConsoleCaptureLines(): ConsoleCaptureLine[] {
  pruneConsoleRing();
  return ring.filter((l) => !isNoiseLine(l.text));
}

export function formatConsoleCaptureSection(): string {
  const lines = getConsoleCaptureLines();
  if (!lines.length) return '(no console output captured)\n';
  return lines
    .map((l) => {
      const t = new Date(l.ts).toISOString();
      return `[${t}] [${l.level}] ${l.text}`;
    })
    .join('\n');
}
